import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Progress, Row, Table } from "reactstrap";
import * as moment from "moment";
import SimpleBar from "simplebar-react";
import FileIcon from "Components/Common/FileIcon";
import Permission from "Components/Common/Permission";

import LTAStatusBadge from "../components/LTAStatusBadge";
import { daysRemaining, termProgress } from "../components/ltaOptions";
import UpdateLTA from "../UpdateLTA";

/**
 * LTA detail view.
 *
 * A left-aligned hybrid: the structure and formality of the paper agreement —
 * letterhead, numbered sections, a register of terms — carried in the
 * conventions of the product: a properties strip, chips, progress and cards.
 * Reading from the left margin keeps long values scannable.
 */

const formatDate = (value?: string | null) =>
  value ? moment(new Date(value)).format("DD MMM Y") : "Not Provided";

const formatMoney = (value?: number | null) =>
  typeof value === "number" ? `$${value.toLocaleString()}` : null;

const NotProvided = () => <span className="text-muted">Not Provided</span>;

/** Sums the value of every contract awarded under the agreement. */
const awardedTotal = (contracts: any[]) =>
  (contracts || []).reduce(
    (sum: number, c: any) => sum + (c?.contractValue || 0),
    0
  );

/**
 * The agencies drawing on the agreement: those recorded on the LTA itself plus
 * any agency that has issued a contract under it. An agency can appear in both
 * places, so entries are keyed by id (falling back to name for records that
 * arrive without one) and the first occurrence wins.
 */
const usingAgencies = (lta: any) => {
  const agencies = [
    ...(lta?.agencies || []),
    ...(lta?.contracts || []).map((c: any) => c?.agency),
  ];
  const seen = new Map<string, any>();
  agencies.forEach((agency: any) => {
    const key = String(agency?.id ?? agency?.name ?? "");
    if (!agency || !key || seen.has(key)) return;
    seen.set(key, agency);
  });
  return Array.from(seen.values());
};

/**
 * Agencies configure their own logo box in `mainLogoLightDimensions`. The
 * values arrive as either numbers or strings depending on how the record was
 * saved, and the aspect ratios differ widely between agencies, so they are
 * coerced and applied as an explicit box with `contain`.
 */
const logoBox = (agency: any) => {
  const dims = agency?.mainLogoLightDimensions || {};
  const toPx = (v: any, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  return { width: toPx(dims.width, 160), height: toPx(dims.height, 100) };
};

/** Full-size asset rather than the thumbnail, so it stays crisp at these sizes. */
const mediaUrl = (media: any) =>
  media?.url ? `${process.env.REACT_APP_FILES_URL}${media.url}` : "";

const AgencyLogo = ({ agency }: { agency: any }) => {
  const { width, height } = logoBox(agency);
  const dark = mediaUrl(agency?.mainLogo);
  const light = mediaUrl(agency?.mainLogoLight);
  if (!dark && !light) return null;

  const style = {
    width,
    height,
    objectFit: "contain" as const,
    maxWidth: "100%",
  };

  return (
    <>
      {dark && (
        <img
          src={dark}
          alt={`${agency?.name || "Agency"} logo`}
          className="card-logo card-logo-dark"
          style={style}
        />
      )}
      {light && (
        <img
          src={light}
          alt={`${agency?.name || "Agency"} logo`}
          className="card-logo card-logo-light"
          style={style}
        />
      )}
    </>
  );
};

/** Numbered section heading with a left accent — document order, product styling. */
const Section = ({
  n,
  title,
  children,
}: {
  n: string;
  title: string;
  children: any;
}) => (
  <section className="mb-4">
    <div className="d-flex align-items-center gap-2 mb-3">
      <span
        className="text-primary fw-bold"
        style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}
      >
        {n}
      </span>
      <h6
        className="mb-0 text-uppercase fw-semibold"
        style={{ letterSpacing: ".1em", fontSize: 12 }}
      >
        {title}
      </h6>
      <span className="border-top flex-grow-1 ms-2" />
    </div>
    {children}
  </section>
);

/** One cell of the properties strip. */
const Property = ({ label, children }: { label: string; children: any }) => (
  <Col md={3} xs={6}>
    <p
      className="text-muted text-uppercase mb-1"
      style={{ fontSize: 10, letterSpacing: ".12em" }}
    >
      {label}
    </p>
    <div className="fw-medium">{children}</div>
  </Col>
);

const Detail = ({ label, children }: { label: string; children: any }) => (
  <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
    <span className="text-muted fs-13">{label}</span>
    <span className="fs-13 text-end">{children}</span>
  </div>
);

const LTAContent = ({
  lta,
  onChanged,
  controller,
  UpdateComponent = UpdateLTA,
}: {
  lta: any;
  onChanged: () => void;
  /** Permission controller to check against - "agency" on the agency-scoped view. */
  controller?: string;
  /** Swapped for the agency-scoped update modal on the agency-scoped view. */
  UpdateComponent?: React.ComponentType<{
    toggle: () => void;
    isModalOpen: boolean;
    selectedRecord: any;
    fetchData: () => void;
  }>;
}) => {
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const contracts = lta?.contracts || [];
  const agencies = usingAgencies(lta);
  const awarded = awardedTotal(contracts);
  const progress = termProgress(lta?.startDate, lta?.endDate);
  const remaining = daysRemaining(lta?.endDate);

  // Numbered on the fly so an absent optional section leaves no gap.
  let n = 0;
  const step = () => String(++n).padStart(2, "0");

  return (
    <React.Fragment>
      <Row>
        <Col xxl={9}>
          <Card className="shadow-sm">
            {/*
              Letterhead. The masthead row carries the agency mark and the page
              action; the agreement's own identity sits on its own line beneath
              so the title has the full width and is not squeezed by the logo.
            */}
            <CardBody className="p-4 border-bottom">
              <div className="d-flex align-items-start justify-content-between gap-3">
                <AgencyLogo agency={lta?.leadAgency} />

                <Permission resource="lta" controller={controller} action="update">
                  <button
                    type="button"
                    className="btn btn-soft-primary btn-sm flex-shrink-0"
                    onClick={() => setIsUpdateModalOpen(true)}
                  >
                    <i className="ri-pencil-line align-bottom me-1" />
                    Edit Agreement
                  </button>
                </Permission>
              </div>

              <div className="mt-3">
                <p
                  className="text-muted text-uppercase mb-1"
                  style={{ letterSpacing: ".2em", fontSize: 10 }}
                >
                  {lta?.leadAgency?.name || "United Nations"} · Long Term
                  Agreement
                </p>
                <h5 className="mb-2">{lta?.title}</h5>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="badge bg-light text-body border fs-12">
                    <i className="ri-hashtag align-bottom" />
                    {lta?.ltaNumber || "No number"}
                  </span>
                  {lta?.ltaType?.name && (
                    <span className="badge bg-secondary-subtle text-secondary fs-12">
                      {lta.ltaType.name}
                    </span>
                  )}
                  <LTAStatusBadge
                    startDate={lta?.startDate}
                    endDate={lta?.endDate}
                    showCountdown
                  />
                </div>
              </div>
            </CardBody>

            {/* Properties strip */}
            <CardBody className="p-4 border-bottom bg-light-subtle">
              <Row className="g-4">
                <Property label="Agreement Value">
                  <span className="fs-18">
                    {formatMoney(lta?.ltaValue) || "Not Provided"}
                  </span>
                </Property>
                <Property label="Term">
                  {formatDate(lta?.startDate)} → {formatDate(lta?.endDate)}
                  {progress !== null && (
                    <>
                      <Progress
                        value={progress}
                        color={
                          progress >= 90
                            ? "danger"
                            : progress >= 75
                            ? "warning"
                            : "success"
                        }
                        style={{ height: "5px" }}
                        className="mt-2"
                      />
                      <span className="text-muted fs-12">
                        {remaining !== null && remaining >= 0
                          ? `${remaining} days remaining`
                          : "Term ended"}
                      </span>
                    </>
                  )}
                </Property>
                <Property label="Service Covered">
                  {lta?.ltaService?.name ? (
                    <span className="badge bg-primary-subtle text-primary fs-12">
                      {lta.ltaService.name}
                    </span>
                  ) : (
                    <NotProvided />
                  )}
                </Property>
                <Property label="Region">
                  {lta?.location?.length ? (
                    <div className="d-flex gap-1 flex-wrap">
                      {lta.location.map((r: any) => (
                        <span
                          key={r.id}
                          className="badge bg-info-subtle text-info fs-11"
                        >
                          {r.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <NotProvided />
                  )}
                </Property>
              </Row>
            </CardBody>

            {/* Body */}
            <CardBody className="p-4">
              <Section n={step()} title="Parties">
                <Row className="g-3">
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <p
                        className="text-muted text-uppercase mb-2"
                        style={{ fontSize: 10, letterSpacing: ".12em" }}
                      >
                        Contractor
                      </p>
                      <p className="fw-medium mb-2">
                        {lta?.contractor?.id ? (
                          <Link to={`/contractors/${lta.contractor.id}`}>
                            <i className="ri-building-line align-bottom me-1" />
                            {lta.contractor.contractorName}
                          </Link>
                        ) : (
                          <NotProvided />
                        )}
                      </p>
                      <Detail label="Telephone">
                        {lta?.contractor?.businessPhone || <NotProvided />}
                      </Detail>
                      <Detail label="Email">
                        {lta?.contractor?.businessEmail || <NotProvided />}
                      </Detail>
                      <Detail label="Website">
                        {lta?.contractor?.businessWebsite || <NotProvided />}
                      </Detail>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <p
                        className="text-muted text-uppercase mb-2"
                        style={{ fontSize: 10, letterSpacing: ".12em" }}
                      >
                        UN Agencies
                      </p>
                      <Detail label="Lead agency">
                        {lta?.leadAgency?.name || <NotProvided />}
                      </Detail>
                      <Detail label="Open to other agencies">
                        {lta?.isUsableByOtherAgencies ? (
                          <span className="badge bg-success-subtle text-success fs-11">
                            Yes
                          </span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary fs-11">
                            No
                          </span>
                        )}
                      </Detail>
                      <div className="pt-2">
                        <span className="text-muted fs-13 d-block mb-1">
                          Agencies using this LTA
                        </span>
                        {agencies.length ? (
                          <div className="d-flex gap-1 flex-wrap">
                            {agencies.map((a: any) => (
                              <span
                                key={a.id ?? a.name}
                                className="badge bg-primary-subtle text-primary fs-11"
                              >
                                {a.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <NotProvided />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Section>

              <Section
                n={step()}
                title={`Contracts Issued Under this Agreement (${contracts.length})`}
              >
                {contracts.length ? (
                  <div className="table-responsive">
                    <Table className="table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Contract</th>
                          <th>Agency</th>
                          <th>Period</th>
                          <th className="text-end">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contracts.map((c: any) => (
                          <tr key={c.id}>
                            <td>
                              <Link to={`/contracts/${c.id}`}>
                                {c.contractTitle || "Untitled"}
                              </Link>
                              <div className="text-muted fs-12">
                                {c.contractNo || "No number"}
                              </div>
                            </td>
                            <td>{c.agency?.name || "—"}</td>
                            <td className="fs-12">
                              {formatDate(c.startDate)} —{" "}
                              {formatDate(c.endDate)}
                            </td>
                            <td className="text-end">
                              {formatMoney(c.contractValue) || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-top fw-semibold">
                          <td colSpan={3}>Total awarded</td>
                          <td className="text-end">
                            ${awarded.toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                ) : (
                  <div className="border border-dashed rounded p-4 text-center">
                    <div className="avatar-sm mx-auto mb-2">
                      <div className="avatar-title bg-light text-secondary rounded fs-24">
                        <i className="ri-file-list-3-line" />
                      </div>
                    </div>
                    <p className="text-muted mb-0">
                      No contracts have been issued under this agreement yet.
                    </p>
                  </div>
                )}
              </Section>

              <Section n={step()} title="Documents &amp; Resources">
                {lta?.ltaDocuments?.length ? (
                  <Row className="g-3">
                    {lta.ltaDocuments.map((doc: any) => {
                      const href = mediaUrl(doc);
                      return (
                        <Col lg={6} key={doc.id}>
                          <div className="border rounded p-2">
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 me-3">
                                <div className="avatar-sm">
                                  <div className="avatar-title bg-light text-secondary rounded fs-24">
                                    <FileIcon fileName={doc.name} />
                                  </div>
                                </div>
                              </div>
                              <div className="flex-grow-1 overflow-hidden">
                                <h6 className="fs-13 mb-1 text-truncate">
                                  <Link
                                    to={href}
                                    target="_blank"
                                    className="text-body"
                                  >
                                    {doc.name}
                                  </Link>
                                </h6>
                                <span className="text-muted fs-12">
                                  {doc.size ? `${doc.size} KB` : ""}
                                </span>
                              </div>
                              <div className="flex-shrink-0 ms-2 d-print-none">
                                <Link
                                  to={href}
                                  target="_blank"
                                  className="btn btn-icon text-muted btn-sm fs-18"
                                >
                                  <i className="ri-download-2-line" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <div className="border border-dashed rounded p-4 text-center">
                    <p className="text-muted mb-0">
                      No documents are attached to this agreement.
                    </p>
                  </div>
                )}
              </Section>

              {lta?.additionalInformation && (
                <Section n={step()} title="Additional Provisions">
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {lta.additionalInformation}
                  </p>
                </Section>
              )}

              <div className="mt-4">
                <div className="alert alert-info mb-0">
                  <p className="mb-0">
                    <span className="fw-semibold">NOTE:</span>
                    <span>
                      {" "}
                      The data provided herein is shared by various agencies and
                      is intended solely for use within the United Nations
                      system. Unauthorized dissemination or distribution of this
                      data outside of the UN system is strictly prohibited.
                    </span>
                  </p>
                </div>
              </div>

              <p
                className="text-muted mb-0 mt-4 pt-3 border-top"
                style={{ fontSize: 11 }}
              >
                Recorded in the Contractor Information Management System · Last
                updated {formatDate(lta?.updatedAt)}
              </p>
            </CardBody>
          </Card>
        </Col>

        {/* Activity rail */}
        <Col xxl={3} className="mb-4">
          <Card className="h-100">
            <CardBody className="p-0">
              <div className="p-3">
                <h6 className="text-muted mb-0 text-uppercase fw-semibold">
                  Activity
                </h6>
              </div>
              <SimpleBar style={{ maxHeight: "410px" }} className="p-3 pt-0">
                <div className="acitivity-timeline acitivity-main">
                  <div className="acitivity-item d-flex">
                    <div className="flex-shrink-0 avatar-xs acitivity-avatar">
                      <div className="avatar-title bg-warning-subtle text-warning rounded-circle">
                        <i className="ri-time-line" />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-1 lh-base">Last Updated</h6>
                      <p className="text-muted mb-1">
                        {lta?.updatedby
                          ? `By ${lta.updatedby.firstName} ${lta.updatedby.lastName}${
                              lta.updatedby.agency?.name
                                ? ` - ${lta.updatedby.agency.name}`
                                : ""
                            }`
                          : "Unknown"}
                      </p>
                      <small className="mb-0 text-muted">
                        {formatDate(lta?.updatedAt)}
                      </small>
                    </div>
                  </div>
                  <div className="acitivity-item py-3 d-flex">
                    <div className="flex-shrink-0 avatar-xs acitivity-avatar">
                      <div className="avatar-title bg-primary-subtle text-primary rounded-circle">
                        <i className="ri-calendar-line" />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-1 lh-base">Created</h6>
                      <p className="text-muted mb-1">
                        {lta?.createdby
                          ? `By ${lta.createdby.firstName} ${lta.createdby.lastName}${
                              lta.createdby.agency?.name
                                ? ` - ${lta.createdby.agency.name}`
                                : ""
                            }`
                          : "Unknown"}
                      </p>
                      <p className="mb-0 text-muted">
                        <small>{formatDate(lta?.createdAt)}</small>
                      </p>
                    </div>
                  </div>
                </div>
              </SimpleBar>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <UpdateComponent
        toggle={() => setIsUpdateModalOpen((state) => !state)}
        isModalOpen={isUpdateModalOpen}
        selectedRecord={lta}
        fetchData={onChanged}
      />
    </React.Fragment>
  );
};

export default LTAContent;
